/**
 * chartLiveData — re-resolve chart series values against LIVE sheet data.
 *
 * Parsed charts carry the file's CACHED series values plus (when present)
 * the source-range formula, e.g. "Sheet1!$B$2:$B$13".  After a reader edits
 * cells, the cache is stale — this module recomputes each series' data from
 * the current sheet model so graphs reflect the edits (QA #4).
 *
 * Anything that can't be resolved (multi-area refs, missing sheet, no ref)
 * keeps its cached values — rendering never breaks on exotic charts.
 */

function colLabelToIndex(label) {
  let n = 0;
  for (let i = 0; i < label.length; i += 1) {
    n = n * 26 + (label.toUpperCase().charCodeAt(i) - 64);
  }
  return n - 1;
}

// "$B$2" / "B2" -> { row, col } (0-based) or null.
function parseCellRef(ref) {
  const m = /^\$?([A-Za-z]+)\$?(\d+)$/.exec(String(ref).trim());
  if (!m) return null;
  return { row: parseInt(m[2], 10) - 1, col: colLabelToIndex(m[1]) };
}

/**
 * "Sheet1!$B$2:$B$13" or "'My Sheet'!$B$2:$B$13" ->
 * { sheet, r1, c1, r2, c2 } or null (multi-area refs are rejected).
 */
export function parseSheetRange(ref) {
  if (!ref || String(ref).indexOf(",") !== -1) return null;
  const m = /^(?:'((?:[^']|'')+)'|([^'!]+))!(\$?[A-Za-z]+\$?\d+)(?::(\$?[A-Za-z]+\$?\d+))?$/.exec(String(ref).trim());
  if (!m) return null;
  const sheet = m[1] ? m[1].replace(/''/g, "'") : m[2];
  const a = parseCellRef(m[3]);
  const b = m[4] ? parseCellRef(m[4]) : a;
  if (!a || !b) return null;
  return {
    sheet,
    r1: Math.min(a.row, b.row), r2: Math.max(a.row, b.row),
    c1: Math.min(a.col, b.col), c2: Math.max(a.col, b.col),
  };
}

// Walk the range row-major and coerce cell values to numbers (blank/text -> 0,
// matching how the numCache reader fills gaps).
function readRangeNumbers(rows, rng) {
  const out = [];
  for (let r = rng.r1; r <= rng.r2; r += 1) {
    for (let c = rng.c1; c <= rng.c2; c += 1) {
      const v = rows[r] ? rows[r][c] : null;
      const num = typeof v === "number" ? v : parseFloat(v);
      out.push(Number.isFinite(num) ? num : 0);
    }
  }
  return out;
}

/**
 * Return the charts array with every resolvable series' data replaced by the
 * live values from `sheetDataByName`.  Charts/series without a usable ref
 * are passed through unchanged (same object identity when nothing changed).
 */
export function withLiveChartData(charts, sheetDataByName) {
  if (!Array.isArray(charts) || charts.length === 0 || !sheetDataByName) return charts;
  return charts.map((chart) => {
    if (!chart || !Array.isArray(chart.series) || chart.series.length === 0) return chart;
    let changed = false;
    const series = chart.series.map((s) => {
      if (!s || !s.valuesRef) return s;
      const rng = parseSheetRange(s.valuesRef);
      if (!rng) return s;
      const model = sheetDataByName[rng.sheet];
      if (!model || !Array.isArray(model.rows)) return s;
      const live = readRangeNumbers(model.rows, rng);
      if (live.length === 0) return s;
      const same =
        live.length === (s.data || []).length &&
        live.every((v, i) => v === s.data[i]);
      if (same) return s;
      changed = true;
      return { ...s, data: live };
    });
    return changed ? { ...chart, series } : chart;
  });
}

export default withLiveChartData;
